/**
 * asof — pure client-side "as-of" reconstruction for the farm object view
 * (#1913 C). Fetch the attribute-transition series ONCE, then fold the
 * displayed state client-side per step — never a per-step network call
 * (the server ``GET .../as-of`` endpoint exists for agents/API completeness
 * and single-point queries; see docs/architecture/TWIN_TIMESERIES.md §5.2).
 *
 * The reconstruction is the SAME algorithm the server runs in UDB
 * ``get_entity_as_of``: take the CURRENT attributes and reverse-patch every
 * transition with ``valid_from > T`` (newest-first — apply ``old_value``; an
 * ABSENT old value removes the key). The boundary is inclusive: a transition
 * at ``valid_from <= T`` stays applied. This parity is asserted directly in
 * ``asof.test.ts`` (client reverse-replay vs an independent forward reference
 * that mirrors the server's documented per-attribute invariant).
 */
import type { AttributeTransition } from '../../../services/twinService';

/** Milliseconds subtracted from the first change to anchor the pristine
 * (pre-recording) state. Any instant strictly before the earliest transition
 * reverts every row; 1s is coarse enough to clear microsecond neighbours yet
 * is still strictly less than the global minimum, so nothing else falls in
 * the gap. */
const PRISTINE_ANCHOR_LEAD_MS = 1000;

export interface AsOfStop {
  /** The instant to store in ``?asof=`` — reconstruct state with T = ``at``. */
  at: string;
  /** The change instant this stop sits immediately BEFORE (its upper bound);
   * the stop reconstructs the state that held just before this change. */
  changeAt: string;
}

/** Parse an ISO instant to epoch ms. All twin timestamps are UTC; a value
 * without a zone is read as UTC by ``Date.parse`` only when it carries a
 * ``Z``/offset — the anchors this module emits always do. */
function ms(iso: string): number {
  return Date.parse(iso);
}

// ISO-8601 shapes that ``?asof=`` may carry — the ONLY formats our anchors
// emit (a ``valid_from`` value or ``Date#toISOString`` output). Matched
// strictly rather than trusting ``Date.parse``: V8 leniently parses human
// formats (``Date.parse('10.07.2026')`` returns a wrong instant, not ``NaN``),
// and it reads a ZONE-LESS date-time as the viewer's LOCAL time — shifting the
// instant. So a date-time MUST carry a ``Z``/±hh:mm offset (Postgres
// ``timestamptz`` renders +00:00); a bare calendar date is allowed (the ES
// spec fixes it to UTC midnight, unambiguous).
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;

/** Last calendar day of a 1-based month (UTC), for the overflow guard. */
function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

/**
 * The single "is this a real historical instant?" predicate shared by the
 * reconstruction, the stepper and the property grid (codex round-2 #1/#6,
 * round-3 #8). A missing, empty, non-ISO, zone-less, or calendar-overflowing
 * (``2026-02-30``) ``?asof=`` normalises to ``null`` = LIVE, so a bad param can
 * never silently reverse the whole series into a fake pristine baseline or
 * shift the instant by the viewer's timezone. Returns the string unchanged
 * only when it is a genuine, unambiguous UTC instant.
 */
export function normalizeAsOf(asOf: string | null | undefined): string | null {
  if (!asOf) return null;
  const m = ISO_DATE_RE.exec(asOf) ?? ISO_DATETIME_RE.exec(asOf);
  if (!m) return null; // non-ISO, or a zone-less date-time
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null; // 2026-02-30 → reject
  return Number.isNaN(Date.parse(asOf)) ? null : asOf;
}

/** Distinct ``valid_from`` instants, ascending. */
function distinctInstantsAsc(transitions: AttributeTransition[]): string[] {
  const seen = new Set<string>();
  for (const t of transitions) seen.add(t.valid_from);
  return [...seen].sort((a, b) => ms(a) - ms(b));
}

/**
 * Reconstruct an entity's attributes at instant ``asOf`` (server-parity with
 * UDB ``get_entity_as_of``). Applies the ``old_value`` of every transition
 * with ``valid_from > asOf``, newest-first, so the OLDEST post-T transition
 * per attribute wins — exactly the value the attribute held at T. An absent
 * old value (``old_value_absent``) removes the key. ``asOf`` null/empty ⇒ the
 * live attributes, returned as a fresh copy (never mutates ``current``).
 */
export function reconstructAttributesAsOf(
  current: Record<string, unknown>,
  transitions: AttributeTransition[],
  asOf: string | null,
): Record<string, unknown> {
  const attrs: Record<string, unknown> = { ...current };
  const norm = normalizeAsOf(asOf);
  if (norm === null) return attrs; // live (incl. empty/unparseable — never a false baseline)
  const t = ms(norm);
  // Sort newest-first (valid_from DESC, id DESC) — the exact order the server
  // applies reverts — so a per-attribute last-write-wins lands the oldest
  // post-T value regardless of the caller's array order.
  const ordered = [...transitions].sort((a, b) => ms(b.valid_from) - ms(a.valid_from) || b.id - a.id);
  for (const tr of ordered) {
    if (ms(tr.valid_from) <= t) continue;
    if (tr.old_value_absent) delete attrs[tr.attribute];
    else attrs[tr.attribute] = tr.old_value;
  }
  return attrs;
}

/**
 * The as-of stepper's stops, newest change first. One stop per distinct
 * change instant; each reconstructs the state that held immediately BEFORE
 * its ``changeAt``. The oldest stop's ``at`` sits just before the first
 * change, so it reveals the pristine pre-recording baseline. Empty when the
 * entity has no recorded transitions (nothing to travel through).
 */
export function deriveAsOfStops(transitions: AttributeTransition[]): AsOfStop[] {
  const distinct = distinctInstantsAsc(transitions);
  const stops: AsOfStop[] = [];
  for (let i = distinct.length - 1; i >= 0; i--) {
    const changeAt = distinct[i];
    // "Just before change i": revert everything with valid_from > at, where
    // at is the previous distinct instant (so change i itself reverts, the
    // rest stay). Before the very first change, anchor a hair earlier.
    const at = i >= 1 ? distinct[i - 1] : new Date(ms(distinct[0]) - PRISTINE_ANCHOR_LEAD_MS).toISOString();
    stops.push({ at, changeAt });
  }
  return stops;
}

/**
 * The source-document id that governs ``attribute``'s value at ``asOf`` — the
 * newest transition for that attribute with ``valid_from <= asOf`` (its
 * ``new_value`` IS the value shown, so its ``source_id`` is that value's
 * provenance). ``null`` when the value predates any recorded transition
 * (pristine baseline), was removed at that time, or the governing transition
 * carried no provenance. ``asOf`` null ⇒ governed by the newest transition
 * overall (the live value).
 */
export function provenanceDocAt(
  transitions: AttributeTransition[],
  attribute: string,
  asOf: string | null,
): string | null {
  const norm = normalizeAsOf(asOf);
  const t = norm ? ms(norm) : Number.POSITIVE_INFINITY;
  const governing = transitions
    .filter((tr) => tr.attribute === attribute && ms(tr.valid_from) <= t)
    .sort((a, b) => ms(b.valid_from) - ms(a.valid_from) || b.id - a.id)[0];
  if (!governing) return null;
  if (governing.new_value_absent) return null;
  return governing.source_id;
}

/**
 * The newest recorded ``valid_from`` across the series, in epoch ms, or null for
 * an empty series. A REDUCE, never ``Math.max(...transitions.map(…))`` (round-3
 * #5): the complete series can hold up to 5M rows and a spread that wide
 * overflows the engine's argument limit (``RangeError``). Order-independent — the
 * caller may pass page 1 (server newest-first) or the full series; either yields
 * the same global maximum.
 */
export function newestInstantMs(transitions: AttributeTransition[]): number | null {
  if (transitions.length === 0) return null;
  let max = Number.NEGATIVE_INFINITY;
  for (const tr of transitions) {
    const at = ms(tr.valid_from);
    if (at > max) max = at;
  }
  return max;
}

/** Recorded transition count per attribute. */
export function transitionCountByAttribute(transitions: AttributeTransition[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const tr of transitions) counts.set(tr.attribute, (counts.get(tr.attribute) ?? 0) + 1);
  return counts;
}

/**
 * Whether reverting an attribute's MOST RECENT change reveals a real prior
 * value — gates the PropertyGrid "Vorherigen Wert anzeigen" affordance
 * (codex round-2 #10). A pure first-appearance (the newest transition's
 * ``old_value`` is absent) has no previous value to show, so the affordance
 * is suppressed rather than jumping to a state where the row simply vanishes.
 */
export function hasRevealablePrior(transitions: AttributeTransition[], attribute: string): boolean {
  let latest: AttributeTransition | null = null;
  for (const tr of transitions) {
    if (tr.attribute !== attribute) continue;
    if (
      latest === null ||
      ms(tr.valid_from) > ms(latest.valid_from) ||
      (ms(tr.valid_from) === ms(latest.valid_from) && tr.id > latest.id)
    ) {
      latest = tr;
    }
  }
  return latest !== null && !latest.old_value_absent;
}

/**
 * The stop that reveals what ``attribute`` held just before its MOST RECENT
 * change — the target of the PropertyGrid "Vorherigen Wert anzeigen"
 * affordance. ``null`` when the attribute has no recorded history.
 */
export function stopBeforeLastChange(
  transitions: AttributeTransition[],
  attribute: string,
): AsOfStop | null {
  let lastChange: string | null = null;
  for (const tr of transitions) {
    if (tr.attribute !== attribute) continue;
    if (lastChange === null || ms(tr.valid_from) > ms(lastChange)) lastChange = tr.valid_from;
  }
  if (lastChange === null) return null;
  return deriveAsOfStops(transitions).find((s) => s.changeAt === lastChange) ?? null;
}

/** Per-attribute display metadata the property grid consumes. Structurally
 * identical to PropertyGrid's ``AttributeHistoryMeta`` (kept here so ``asof`` has
 * no page-component import). */
export interface AttributeAsOfMeta {
  /**
   * Whether to offer the per-attribute "Vorherigen Wert anzeigen" affordance.
   * Only in LIVE mode (#1973 finding 6): the jump targets the attribute's GLOBAL
   * last change, which from a historical vantage can move FORWARD in time, so the
   * affordance is suppressed while time-travelling.
   */
  canReveal: boolean;
  /**
   * The governing transition's raw ``source_id`` for the displayed value —
   * ``null`` when pristine (pre-recorder), removed, or provenance-less.
   *
   * Named for what it IS, not what it was assumed to be (#2791): the column
   * is overloaded — the document lane writes a real ``document_id`` while the
   * structured backfill lanes stamp a source-SYSTEM token
   * (``wiberry-mysql-backfill``). Resolving which is which is the renderer's
   * job (``provenanceSource.resolveProvenanceSource``); this stays raw.
   *
   * Surfaced in BOTH modes: historical (the value governed at ``asOf``) and
   * live (the newest transition overall governs the live value). #1913 C
   * gated this to time-travel only; #2791 unlocks live, which is where the
   * "which system produced this number?" question is actually asked.
   */
  provenanceSourceId: string | null;
}

/**
 * Per-attribute display metadata for the property grid, computed in ONE pass
 * over the transition series (#1973 finding 9 — replaces the previous
 * O(attributes × transitions) fan-out of ``transitionCountByAttribute`` +
 * ``hasRevealablePrior`` + ``provenanceDocAt`` called per attribute). For each
 * attribute the sweep tracks the latest transition overall (→ ``canReveal``, via
 * its ``old_value``) and the governing transition at-or-before ``asOf``
 * (→ provenance, via its ``source_id``). A null/live ``asOf`` enables the reveal
 * affordance; a real historical ``asOf`` suppresses it (finding 6).
 *
 * Provenance is surfaced in BOTH modes (#2791). Live means ``t = +Infinity``,
 * so ``governing`` collapses onto ``latest`` — the transition that wrote the
 * value currently displayed, which is exactly the live value's source. No
 * extra pass and no extra fetch: the same single sweep already computed it.
 */
export function attributeHistoryMeta(
  transitions: AttributeTransition[],
  asOf: string | null,
): Map<string, AttributeAsOfMeta> {
  const norm = normalizeAsOf(asOf);
  const isHistorical = norm !== null;
  const t = isHistorical ? ms(norm) : Number.POSITIVE_INFINITY;
  const isNewer = (a: AttributeTransition, b: AttributeTransition): boolean =>
    ms(a.valid_from) > ms(b.valid_from) || (ms(a.valid_from) === ms(b.valid_from) && a.id > b.id);
  const latest = new Map<string, AttributeTransition>();
  const governing = new Map<string, AttributeTransition>();
  for (const tr of transitions) {
    const curLatest = latest.get(tr.attribute);
    if (!curLatest || isNewer(tr, curLatest)) latest.set(tr.attribute, tr);
    if (ms(tr.valid_from) <= t) {
      const curGov = governing.get(tr.attribute);
      if (!curGov || isNewer(tr, curGov)) governing.set(tr.attribute, tr);
    }
  }
  const meta = new Map<string, AttributeAsOfMeta>();
  for (const [attribute, latestTr] of latest) {
    const gov = governing.get(attribute);
    meta.set(attribute, {
      canReveal: !isHistorical && !latestTr.old_value_absent,
      provenanceSourceId: gov && !gov.new_value_absent ? gov.source_id : null,
    });
  }
  return meta;
}

/**
 * Immutably fold an ``?asof=`` value into an existing query string — the pure
 * write-path FarmPage's as-of navigation uses (a truthy iso sets the param, a
 * null/empty one clears it, returning to the live view). Kept here so the
 * URL round-trip is unit-tested against the exact code the page runs.
 */
export function withAsOfParam(prev: URLSearchParams, iso: string | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (iso) next.set('asof', iso);
  else next.delete('asof');
  return next;
}
